import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit, Copy, ChevronLeft, ChevronRight, ArrowLeft, Check, ChevronDown, ChevronUp, X, Download, AlertTriangle } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';
import BulkSelectBar, { BulkSelectToggle } from '../../components/BulkSelectBar';
import ConfirmDialog from '../../components/ConfirmDialog';
import ExportModal from '../../components/ExportModal';
import { sortedByName } from '../../utils/sortAlpha';
import { downloadCsv, fetchAllPages } from '../../utils/exportCsv';
const TODAY_DATE = new Date().toISOString().split('T')[0];

/* ─── helpers ──────────────────────────────────────────────────────────── */
const formatCurrency = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : 'N/A';

const emptyForm = () => ({
  customer_id: '',
  date: new Date().toISOString().split('T')[0],
  morning_cow_milk: '',
  evening_cow_milk: '',
  morning_buffalo_milk: '',
  evening_buffalo_milk: '',
  // per-bill editable prices (auto-filled from customer)
  cow_milk_price: '',
  buffalo_milk_price: '',
  // chick / fatwa
  showChickFatwa: false,
  chick_quantity: '',
  chick_price_per_unit: '',
  fatwa_quantity: '',
  fatwa_price_per_unit: '',
  // custom items
  custom_items: []
});

function SalesPage() {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [productItems, setProductItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentView, setCurrentView] = useState('list');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm());

  // Add Sale entry mode — 'single' is the existing one-customer form; 'vehicle'
  // bulk-enters every customer on a vehicle's route for one date + slot at once.
  // Only relevant when adding (editing always uses the single-customer form).
  const [entryMode, setEntryMode] = useState('single');
  const [vehicleForm, setVehicleForm] = useState({
    date: new Date().toISOString().split('T')[0],
    vehicle_number: '',
    slot: 'morning',
  });
  const [vehicleRows, setVehicleRows] = useState([]);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [vehicleSubmitting, setVehicleSubmitting] = useState(false);
  const [vehicleEditRow, setVehicleEditRow] = useState(null); // customer_id of the row whose overlay is open

  // replicate modal
  const [replicateItem, setReplicateItem] = useState(null);
  const [replicateMode, setReplicateMode] = useState('single'); // 'single' | 'range'
  const [replicateDate, setReplicateDate] = useState(new Date().toISOString().split('T')[0]);
  const [replicateStartDate, setReplicateStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [replicateEndDate, setReplicateEndDate] = useState(new Date().toISOString().split('T')[0]);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 30;

  const [filters, setFilters] = useState({
    customer_id: '', start_date: '', end_date: '', min_amount: '', max_amount: '', status: '', vehicle_number: ''
  });
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc' — by date

  // totals across every record matching the current filters (not just the visible page)
  const [overallStats, setOverallStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // bulk select / delete
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { ids, invoicedCount }

  // export
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    customer_id: '', start_date: '', end_date: '', min_amount: '', max_amount: '', status: '', vehicle_number: ''
  });

  /* ── loaders ─────────────────────────────────────────────────────────── */
  const loadData = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, per_page: itemsPerPage, sort_order: sortOrder,
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''))
      };
      const res = await apiService.searchBills(params);
      setData(res.data || []);
      setCurrentPage(res.pagination?.page || 1);
      setTotalPages(res.pagination?.pages || 1);
      setTotalItems(res.pagination?.total || 0);
    } catch (err) {
      setError('Failed to load sales: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await apiService.getCustomers();
      setCustomers(res.data || []);
    } catch {}
  };

  const loadProductItems = async () => {
    try {
      const res = await apiService.getProductItems();
      // exclude buffalo milk and cow milk defaults (handled separately)
      const customs = (res.data || []).filter(
        i => !i.is_default
      );
      setProductItems(customs);
    } catch {}
  };

  // Aggregates every record matching the current filters (across all pages), not just
  // the page currently on screen — reuses the same paginated search as export.
  const loadOverallStats = async () => {
    try {
      setLoadingStats(true);
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const rows = await fetchAllPages((page, per_page) => apiService.searchBills({ ...params, page, per_page }), { perPage: 200 });

      const sum = (fn) => rows.reduce((s, b) => s + fn(b), 0);
      const morningBuffalo = sum(b => b.morning_buffalo_milk || 0);
      const morningCow = sum(b => b.morning_cow_milk || 0);
      const eveningBuffalo = sum(b => b.evening_buffalo_milk || 0);
      const eveningCow = sum(b => b.evening_cow_milk || 0);

      setOverallStats({
        morningBuffalo,
        morningCow,
        eveningBuffalo,
        eveningCow,
        buffaloTotalL: morningBuffalo + eveningBuffalo,
        cowTotalL: morningCow + eveningCow,
        chickQtyTotal: sum(b => b.chick_quantity || 0),
        fatwaQtyTotal: sum(b => b.fatwa_quantity || 0),
        otherQtyTotal: sum(b => (b.custom_items || []).reduce((cs, ci) => cs + (ci.quantity || 0), 0)),
        sumAmount: sum(b => b.total_amount || 0),
      });
    } catch (err) {
      setOverallStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => { loadCustomers(); loadProductItems(); loadData(); loadOverallStats(); }, []);
  useEffect(() => { loadData(1); }, [filters, sortOrder]);
  useEffect(() => { loadOverallStats(); }, [filters]);

  /* ── vehicle-based bulk entry ─────────────────────────────────────────── */
  const vehicleNumbers = useMemo(() => {
    const set = new Set(customers.map(c => c.vehicle_number).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [customers]);

  const loadVehicleRows = async () => {
    if (!vehicleForm.vehicle_number || !vehicleForm.date || !vehicleForm.slot) {
      setVehicleRows([]);
      return;
    }
    try {
      setVehicleLoading(true);
      const res = await apiService.getVehicleEntry({
        vehicle_number: vehicleForm.vehicle_number,
        date: vehicleForm.date,
        slot: vehicleForm.slot,
      });
      const rows = (res.data?.customers || []).map(c => ({
        customer_id: c.customer_id,
        customer_name: c.customer_name,
        cow_quantity: '',
        buffalo_quantity: '',
        cow_price_override: '',
        buffalo_price_override: '',
        chick_quantity: '',
        chick_price_override: '',
        fatwa_quantity: '',
        fatwa_price_override: '',
        custom_items: [],
        default_cow_price: c.cow_milk_price,
        default_buffalo_price: c.buffalo_milk_price,
        default_chick_price: c.chick_price,
        default_fatwa_price: c.fatwa_price,
        slot_filled: c.slot_filled,
        existing_bill_no: c.existing_bill_no,
      }));
      setVehicleRows(rows);
    } catch (err) {
      toast.error('Failed to load vehicle customers: ' + err.message);
      setVehicleRows([]);
    } finally {
      setVehicleLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'form' && entryMode === 'vehicle' && !editingItem) {
      loadVehicleRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, entryMode, vehicleForm.vehicle_number, vehicleForm.date, vehicleForm.slot]);

  const updateVehicleRow = (customerId, field, value) => {
    setVehicleRows(prev => prev.map(r => (r.customer_id === customerId ? { ...r, [field]: value } : r)));
  };

  const addVehicleRowCustomItem = (customerId, productItem) => {
    setVehicleRows(prev => prev.map(r => (r.customer_id === customerId ? {
      ...r,
      custom_items: [...r.custom_items, {
        product_item_id: productItem.item_id,
        item_name: productItem.name,
        unit: productItem.unit,
        quantity: '',
        price_per_unit: productItem.default_price || ''
      }]
    } : r)));
  };

  const updateVehicleRowCustomItem = (customerId, idx, field, value) => {
    setVehicleRows(prev => prev.map(r => {
      if (r.customer_id !== customerId) return r;
      const items = [...r.custom_items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...r, custom_items: items };
    }));
  };

  const removeVehicleRowCustomItem = (customerId, idx) => {
    setVehicleRows(prev => prev.map(r => (r.customer_id === customerId ? {
      ...r, custom_items: r.custom_items.filter((_, i) => i !== idx)
    } : r)));
  };

  const buildVehicleEntries = () => {
    return vehicleRows.map(r => {
      const cowQty = Number(r.cow_quantity) || 0;
      const bufQty = Number(r.buffalo_quantity) || 0;
      const chickQty = Number(r.chick_quantity) || 0;
      const fatwaQty = Number(r.fatwa_quantity) || 0;
      const customItems = r.custom_items
        .filter(ci => ci.item_name && (Number(ci.quantity) || 0) > 0)
        .map(ci => ({
          product_item_id: ci.product_item_id || null,
          item_name: ci.item_name,
          unit: ci.unit,
          quantity: Number(ci.quantity) || 0,
          price_per_unit: Number(ci.price_per_unit) || 0,
          total: (Number(ci.quantity) || 0) * (Number(ci.price_per_unit) || 0)
        }));
      if (cowQty <= 0 && bufQty <= 0 && chickQty <= 0 && fatwaQty <= 0 && customItems.length === 0) {
        return null;
      }
      return {
        customer_id: r.customer_id,
        cow_quantity: cowQty,
        buffalo_quantity: bufQty,
        cow_milk_price: r.cow_price_override !== '' ? Number(r.cow_price_override) : null,
        buffalo_milk_price: r.buffalo_price_override !== '' ? Number(r.buffalo_price_override) : null,
        chick_quantity: chickQty,
        chick_price_per_unit: r.chick_price_override !== '' ? Number(r.chick_price_override) : null,
        fatwa_quantity: fatwaQty,
        fatwa_price_per_unit: r.fatwa_price_override !== '' ? Number(r.fatwa_price_override) : null,
        custom_items: customItems,
      };
    }).filter(Boolean);
  };

  const handleVehicleSubmit = async () => {
    if (!vehicleForm.vehicle_number) { toast.warning('Please select a vehicle number.'); return; }
    if (!vehicleForm.date) { toast.warning('Please select a date.'); return; }

    const entries = buildVehicleEntries();
    if (entries.length === 0) { toast.warning('Enter at least one customer\'s quantity before saving.'); return; }

    try {
      setVehicleSubmitting(true);
      let res = await apiService.bulkCreateVehicleBills({
        vehicle_number: vehicleForm.vehicle_number,
        date: vehicleForm.date,
        slot: vehicleForm.slot,
        entries,
      });

      let conflicts = res.data?.conflicts || [];
      if (conflicts.length > 0) {
        const names = conflicts.map(c => c.customer_name).join(', ');
        const proceed = window.confirm(
          `${names} already ${conflicts.length === 1 ? 'has' : 'have'} a ${vehicleForm.slot} entry on ${formatDate(vehicleForm.date)}. Overwrite with the new values?`
        );
        if (proceed) {
          const forceIds = conflicts.map(c => c.customer_id);
          const retryEntries = entries.filter(e => forceIds.includes(e.customer_id));
          res = await apiService.bulkCreateVehicleBills({
            vehicle_number: vehicleForm.vehicle_number,
            date: vehicleForm.date,
            slot: vehicleForm.slot,
            entries: retryEntries,
            force_customer_ids: forceIds,
          });
        }
      }

      const savedCount = (res.data?.created || []).length + (res.data?.merged || []).length;
      toast.success(`${savedCount} sale entr${savedCount === 1 ? 'y' : 'ies'} saved successfully`);

      await loadData(currentPage);
      await loadOverallStats();
      setCurrentView('list');
    } catch (err) {
      toast.error('Failed to save vehicle entries: ' + err.message);
    } finally {
      setVehicleSubmitting(false);
    }
  };

  /* ── price auto-fill when customer changes ──────────────────────────── */
  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.customer_id === Number(customerId));
    setFormData(prev => ({
      ...prev,
      customer_id: customerId,
      cow_milk_price: customer?.cow_milk_price ?? '',
      buffalo_milk_price: customer?.buffalo_milk_price ?? '',
      chick_price_per_unit: customer?.chick_price ?? prev.chick_price_per_unit,
      fatwa_price_per_unit: customer?.fatwa_price ?? prev.fatwa_price_per_unit,
    }));
  };

  /* ── form helpers ────────────────────────────────────────────────────── */
  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // computed line totals
  const cowTotal = () => {
    const qty = (Number(formData.morning_cow_milk) || 0) + (Number(formData.evening_cow_milk) || 0);
    return qty * (Number(formData.cow_milk_price) || 0);
  };
  const buffaloTotal = () => {
    const qty = (Number(formData.morning_buffalo_milk) || 0) + (Number(formData.evening_buffalo_milk) || 0);
    return qty * (Number(formData.buffalo_milk_price) || 0);
  };
  const chickTotal = () => (Number(formData.chick_quantity) || 0) * (Number(formData.chick_price_per_unit) || 0);
  const fatwaTotal = () => (Number(formData.fatwa_quantity) || 0) * (Number(formData.fatwa_price_per_unit) || 0);
  const customTotal = () => formData.custom_items.reduce((s, ci) => s + (Number(ci.quantity) || 0) * (Number(ci.price_per_unit) || 0), 0);
  const grandTotal = () => cowTotal() + buffaloTotal() + chickTotal() + fatwaTotal() + customTotal();

  /* ── custom item helpers ─────────────────────────────────────────────── */
  const addCustomItem = (productItem) => {
    setFormData(prev => ({
      ...prev,
      custom_items: [...prev.custom_items, {
        product_item_id: productItem.item_id,
        item_name: productItem.name,
        unit: productItem.unit,
        quantity: '',
        price_per_unit: productItem.default_price || ''
      }]
    }));
  };

  const updateCustomItem = (idx, field, value) => {
    setFormData(prev => {
      const items = [...prev.custom_items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, custom_items: items };
    });
  };

  const removeCustomItem = (idx) => {
    setFormData(prev => ({
      ...prev,
      custom_items: prev.custom_items.filter((_, i) => i !== idx)
    }));
  };

  /* ── CRUD actions ────────────────────────────────────────────────────── */
  const handleAdd = () => {
    setEditingItem(null);
    setFormData(emptyForm());
    setEntryMode('single');
    setVehicleRows([]);
    setCurrentView('form');
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setEntryMode('single');
    const customer = customers.find(c => c.customer_id === item.customer_id);
    setFormData({
      customer_id: item.customer_id?.toString() || '',
      date: item.date || new Date().toISOString().split('T')[0],
      morning_cow_milk: item.morning_cow_milk?.toString() || '',
      evening_cow_milk: item.evening_cow_milk?.toString() || '',
      morning_buffalo_milk: item.morning_buffalo_milk?.toString() || '',
      evening_buffalo_milk: item.evening_buffalo_milk?.toString() || '',
      cow_milk_price: item.cow_milk_price ?? customer?.cow_milk_price ?? '',
      buffalo_milk_price: item.buffalo_milk_price ?? customer?.buffalo_milk_price ?? '',
      showChickFatwa: !!(item.chick_quantity || item.fatwa_quantity),
      chick_quantity: item.chick_quantity?.toString() || '',
      chick_price_per_unit: item.chick_price_per_unit?.toString() || customer?.chick_price?.toString() || '',
      fatwa_quantity: item.fatwa_quantity?.toString() || '',
      fatwa_price_per_unit: item.fatwa_price_per_unit?.toString() || customer?.fatwa_price?.toString() || '',
      custom_items: (item.custom_items || []).map(ci => ({
        product_item_id: ci.product_item_id,
        item_name: ci.item_name,
        unit: ci.unit,
        quantity: ci.quantity?.toString() || '',
        price_per_unit: ci.price_per_unit?.toString() || ''
      }))
    });
    setCurrentView('form');
  };

  /* ── bulk select / delete ────────────────────────────────────────────── */
  const enterSelectMode = () => { setSelectMode(true); setSelectedIds(new Set()); };
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };

  const toggleRow = (billNo) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(billNo)) next.delete(billNo); else next.add(billNo);
      return next;
    });
  };

  const allSelected = data.length > 0 && data.every(item => selectedIds.has(item.bill_no));
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(data.map(item => item.bill_no)));
  };

  const requestBulkDelete = () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const invoicedCount = data.filter(item => selectedIds.has(item.bill_no) && item.invoice_id).length;
    setConfirmDelete({ ids, invoicedCount });
  };

  const confirmBulkDelete = async () => {
    if (!confirmDelete) return;
    try {
      setBulkDeleting(true);
      await apiService.bulkDeleteBills(confirmDelete.ids);
      toast.success(`${confirmDelete.ids.length} sale record(s) deleted successfully`);
      setConfirmDelete(null);
      exitSelectMode();
      await loadData(currentPage);
      await loadOverallStats();
    } catch (err) {
      toast.error('Failed to delete: ' + err.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  /* ── export ──────────────────────────────────────────────────────────── */
  const openExport = () => {
    setExportFilters({ ...filters });
    setShowExport(true);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = { sort_order: sortOrder,
        ...Object.fromEntries(Object.entries(exportFilters).filter(([, v]) => v !== ''))
      };
      const rows = await fetchAllPages((page, per_page) => apiService.searchBills({ ...params, page, per_page }));

      const columns = [
        { header: 'Date', value: r => formatDate(r.date) },
        { header: 'Customer', value: r => getCustomerName(r.customer_id) },
        { header: 'Morning Buffalo (L)', value: r => r.morning_buffalo_milk || 0 },
        { header: 'Morning Cow (L)', value: r => r.morning_cow_milk || 0 },
        { header: 'Evening Buffalo (L)', value: r => r.evening_buffalo_milk || 0 },
        { header: 'Evening Cow (L)', value: r => r.evening_cow_milk || 0 },
        { header: 'Chick', value: r => r.chick_quantity > 0 ? r.chick_quantity : '' },
        { header: 'Fatwa', value: r => r.fatwa_quantity > 0 ? r.fatwa_quantity : '' },
        { header: 'Other Items', value: r => (r.custom_items || []).map(ci => `${ci.item_name} x${ci.quantity}`).join('; ') },
        { header: 'Total', value: r => (r.total_amount || 0).toFixed(2) },
        { header: 'Status', value: r => r.invoice_id ? 'Billed' : 'Unbilled' },
      ];

      downloadCsv(`sales_${new Date().toISOString().slice(0, 10)}.csv`, columns, rows);
      toast.success(`Exported ${rows.length} record(s).`);
      setShowExport(false);
    } catch (err) {
      toast.error('Failed to export: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Returns existing (non-excluded) sale entries for this customer within [startDate, endDate].
  // Fails open (returns []) if the check itself errors — never blocks saving over a network hiccup.
  const findExistingSales = async (customerId, startDate, endDate, excludeBillNo) => {
    try {
      const res = await apiService.searchBills({
        customer_id: customerId, start_date: startDate, end_date: endDate, per_page: 200
      });
      return (res.data || []).filter(b => b.bill_no !== excludeBillNo);
    } catch {
      return [];
    }
  };

  const handleSubmit = async () => {
    if (!formData.customer_id) { toast.warning('Please select a customer.'); return; }
    if (!formData.date) { toast.warning('Please select a date.'); return; }

    const existing = await findExistingSales(Number(formData.customer_id), formData.date, formData.date, editingItem?.bill_no);
    if (existing.length > 0) {
      const proceed = window.confirm(
        `${getCustomerName(Number(formData.customer_id))} already has a sale entry on ${formatDate(formData.date)}. Add this as another entry anyway?`
      );
      if (!proceed) return;
    }

    const payload = {
      customer_id: Number(formData.customer_id),
      date: formData.date,
      morning_cow_milk: Number(formData.morning_cow_milk) || 0,
      evening_cow_milk: Number(formData.evening_cow_milk) || 0,
      morning_buffalo_milk: Number(formData.morning_buffalo_milk) || 0,
      evening_buffalo_milk: Number(formData.evening_buffalo_milk) || 0,
      cow_milk_price: formData.cow_milk_price !== '' ? Number(formData.cow_milk_price) : null,
      buffalo_milk_price: formData.buffalo_milk_price !== '' ? Number(formData.buffalo_milk_price) : null,
      chick_quantity: Number(formData.chick_quantity) || 0,
      chick_price_per_unit: formData.chick_price_per_unit !== '' ? Number(formData.chick_price_per_unit) : null,
      fatwa_quantity: Number(formData.fatwa_quantity) || 0,
      fatwa_price_per_unit: formData.fatwa_price_per_unit !== '' ? Number(formData.fatwa_price_per_unit) : null,
      custom_items: formData.custom_items
        .filter(ci => ci.item_name && (Number(ci.quantity) || 0) > 0)
        .map(ci => ({
          product_item_id: ci.product_item_id || null,
          item_name: ci.item_name,
          unit: ci.unit,
          quantity: Number(ci.quantity) || 0,
          price_per_unit: Number(ci.price_per_unit) || 0,
          total: (Number(ci.quantity) || 0) * (Number(ci.price_per_unit) || 0)
        }))
    };

    try {
      setLoading(true);
      setError(null);
      if (editingItem) {
        await apiService.updateBill(editingItem.bill_no, payload);
        await loadData(currentPage);
        await loadOverallStats();
        setCurrentView('list');
        setEditingItem(null);
      } else {
        await apiService.createBill(payload);
        toast.success('Sale added successfully');
        await loadData(currentPage);
        await loadOverallStats();
        // Stay on the form, ready for the next entry, instead of returning to the list.
        setFormData(emptyForm());
      }
    } catch (err) {
      toast.error('Failed to save: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── replicate ───────────────────────────────────────────────────────── */
  const replicateRangeCount = () => {
    if (!replicateStartDate || !replicateEndDate) return 0;
    const start = new Date(replicateStartDate);
    const end = new Date(replicateEndDate);
    if (end < start) return 0;
    return Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  const buildReplicatePayload = () => ({
    customer_id: replicateItem.customer_id,
    morning_cow_milk: replicateItem.morning_cow_milk || 0,
    evening_cow_milk: replicateItem.evening_cow_milk || 0,
    morning_buffalo_milk: replicateItem.morning_buffalo_milk || 0,
    evening_buffalo_milk: replicateItem.evening_buffalo_milk || 0,
    chick_quantity: replicateItem.chick_quantity || 0,
    chick_price_per_unit: replicateItem.chick_price_per_unit,
    fatwa_quantity: replicateItem.fatwa_quantity || 0,
    fatwa_price_per_unit: replicateItem.fatwa_price_per_unit,
    custom_items: (replicateItem.custom_items || []).map(ci => ({
      product_item_id: ci.product_item_id,
      item_name: ci.item_name,
      unit: ci.unit,
      quantity: ci.quantity,
      price_per_unit: ci.price_per_unit,
      total: ci.total
    }))
  });

  const handleReplicate = async () => {
    if (!replicateItem) return;

    if (replicateMode === 'range') {
      if (!replicateStartDate || !replicateEndDate || replicateRangeCount() <= 0) return;
      const existing = await findExistingSales(replicateItem.customer_id, replicateStartDate, replicateEndDate);
      if (existing.length > 0) {
        const dates = [...new Set(existing.map(b => formatDate(b.date)))].join(', ');
        const proceed = window.confirm(
          `${getCustomerName(replicateItem.customer_id)} already has sale entries on ${existing.length} date(s) in this range (${dates}). Continue and create entries for those dates too?`
        );
        if (!proceed) return;
      }
    } else {
      if (!replicateDate) return;
      const existing = await findExistingSales(replicateItem.customer_id, replicateDate, replicateDate);
      if (existing.length > 0) {
        const proceed = window.confirm(
          `${getCustomerName(replicateItem.customer_id)} already has a sale entry on ${formatDate(replicateDate)}. Add this as another entry anyway?`
        );
        if (!proceed) return;
      }
    }

    try {
      setLoading(true);
      if (replicateMode === 'range') {
        await apiService.replicateBillRange({
          ...buildReplicatePayload(),
          start_date: replicateStartDate,
          end_date: replicateEndDate,
        });
      } else {
        await apiService.createBill({ ...buildReplicatePayload(), date: replicateDate });
      }
      setReplicateItem(null);
      await loadData(currentPage);
      await loadOverallStats();
    } catch (err) {
      toast.error('Failed to replicate: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (id) => {
    const c = customers.find(c => c.customer_id === id);
    return c ? c.name : `Customer ${id}`;
  };

  // Distinguishes same-named customers in dropdowns by showing their address alongside.
  const customerOptionLabel = (c) => {
    if (!c.address) return c.name;
    const addr = c.address.length > 40 ? c.address.slice(0, 40) + '…' : c.address;
    return `${c.name} (${addr})`;
  };

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">{content}</div>
      </div>
    </div>
  );

  /* ──────────────────────────────────────────────────────────────────────
   *  FORM VIEW
   * ────────────────────────────────────────────────────────────────────── */
  if (currentView === 'form') {
    return renderLayout(
      <>
        <div className="page-header">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={() => { setCurrentView('list'); setEditingItem(null); }} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Cancel
            </button>
            <h1 className="page-title">{editingItem ? 'Edit Sale' : 'Add New Sale'}</h1>
          </div>
        </div>

        {error && <div className="error-container"><p>{error}</p></div>}

        {!editingItem && (
          <div className="card" style={{ maxWidth: '860px', margin: '0 auto 1rem' }}>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 500, cursor: 'pointer' }}>
                <input type="radio" name="entryMode" value="single" checked={entryMode === 'single'} onChange={() => setEntryMode('single')} />
                Single Customer
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 500, cursor: 'pointer' }}>
                <input type="radio" name="entryMode" value="vehicle" checked={entryMode === 'vehicle'} onChange={() => setEntryMode('vehicle')} />
                By Vehicle Number
              </label>
            </div>
          </div>
        )}

        {(editingItem || entryMode === 'single') && (
        <div className="card" style={{ maxWidth: '860px', margin: '0 auto' }}>

          {/* ── customer + date ── */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Customer *</label>
              <select
                className="form-select"
                name="customer_id"
                value={formData.customer_id}
                onChange={e => handleCustomerChange(e.target.value)}
                disabled={loading}
              >
                <option value="">Select Customer</option>
                {sortedByName(customers).map(c => (
                  <option key={c.customer_id} value={c.customer_id}>{customerOptionLabel(c)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input type="date" max={TODAY_DATE} className="form-input" name="date" value={formData.date} onChange={handleInputChange} disabled={loading} />
            </div>
          </div>

          {/* ── morning milk ── */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginTop: '0.5rem' }}>
            <h3 style={{ fontSize: '1rem', color: 'var(--primary-color)', marginBottom: '0.75rem' }}>Morning Collection</h3>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Buffalo Milk (L)</label>
              <input type="number" className="form-input" name="morning_buffalo_milk" value={formData.morning_buffalo_milk} onChange={handleInputChange} placeholder="0" min="0" step="0.1" />
            </div>
            <div className="form-group">
              <label className="form-label">Cow Milk (L)</label>
              <input type="number" className="form-input" name="morning_cow_milk" value={formData.morning_cow_milk} onChange={handleInputChange} placeholder="0" min="0" step="0.1" />
            </div>
          </div>

          {/* ── evening milk ── */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginTop: '0.5rem' }}>
            <h3 style={{ fontSize: '1rem', color: 'var(--accent-color)', marginBottom: '0.75rem' }}>Evening Collection</h3>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Buffalo Milk (L)</label>
              <input type="number" className="form-input" name="evening_buffalo_milk" value={formData.evening_buffalo_milk} onChange={handleInputChange} placeholder="0" min="0" step="0.1" />
            </div>
            <div className="form-group">
              <label className="form-label">Cow Milk (L)</label>
              <input type="number" className="form-input" name="evening_cow_milk" value={formData.evening_cow_milk} onChange={handleInputChange} placeholder="0" min="0" step="0.1" />
            </div>
          </div>

          {/* ── prices (auto-filled, editable) ── */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginTop: '0.5rem' }}>
            <h3 style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Milk Prices (auto-filled from customer, editable)</h3>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Buffalo Milk Price (₹/L)</label>
              <input type="number" className="form-input" name="buffalo_milk_price" value={formData.buffalo_milk_price} onChange={handleInputChange} placeholder="0.00" min="0" step="0.01" />
            </div>
            <div className="form-group">
              <label className="form-label">Cow Milk Price (₹/L)</label>
              <input type="number" className="form-input" name="cow_milk_price" value={formData.cow_milk_price} onChange={handleInputChange} placeholder="0.00" min="0" step="0.01" />
            </div>
          </div>

          {/* ── chick / fatwa expandable ── */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem' }}
              onClick={() => setFormData(p => ({ ...p, showChickFatwa: !p.showChickFatwa }))}
            >
              {formData.showChickFatwa ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              Chick / Fatwa (optional)
            </button>
          </div>

          {formData.showChickFatwa && (
            <>
              <div style={{ marginTop: '0.75rem' }}>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Chick</h3>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Quantity</label>
                  <input type="number" className="form-input" name="chick_quantity" value={formData.chick_quantity} onChange={handleInputChange} placeholder="0" min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label className="form-label">Price per unit (₹)</label>
                  <input type="number" className="form-input" name="chick_price_per_unit" value={formData.chick_price_per_unit} onChange={handleInputChange} placeholder="0.00" min="0" step="0.01" />
                </div>
              </div>
              <div style={{ marginTop: '0.25rem' }}>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Fatwa</h3>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Quantity</label>
                  <input type="number" className="form-input" name="fatwa_quantity" value={formData.fatwa_quantity} onChange={handleInputChange} placeholder="0" min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label className="form-label">Price per unit (₹)</label>
                  <input type="number" className="form-input" name="fatwa_price_per_unit" value={formData.fatwa_price_per_unit} onChange={handleInputChange} placeholder="0.00" min="0" step="0.01" />
                </div>
              </div>
            </>
          )}

          {/* ── custom items ── */}
          {productItems.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
              <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Other Items</h3>
              {formData.custom_items.map((ci, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '2', minWidth: '120px' }}>
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Item</label>
                    <input className="form-input" value={ci.item_name} readOnly style={{ background: 'var(--bg-secondary)' }} />
                  </div>
                  <div style={{ flex: '1', minWidth: '80px' }}>
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Qty ({ci.unit})</label>
                    <input type="number" className="form-input" value={ci.quantity} onChange={e => updateCustomItem(idx, 'quantity', e.target.value)} placeholder="0" min="0" step="0.01" />
                  </div>
                  <div style={{ flex: '1', minWidth: '90px' }}>
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Price (₹/{ci.unit})</label>
                    <input type="number" className="form-input" value={ci.price_per_unit} onChange={e => updateCustomItem(idx, 'price_per_unit', e.target.value)} placeholder="0.00" min="0" step="0.01" />
                  </div>
                  <div style={{ flex: '1', minWidth: '80px' }}>
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Total</label>
                    <input className="form-input" readOnly value={formatCurrency((Number(ci.quantity) || 0) * (Number(ci.price_per_unit) || 0))} style={{ background: 'var(--bg-secondary)' }} />
                  </div>
                  <button type="button" className="btn-icon" style={{ color: 'var(--error-color)', marginBottom: '2px' }} onClick={() => removeCustomItem(idx)}>
                    <X size={16} />
                  </button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                {productItems.filter(pi => !formData.custom_items.find(ci => ci.product_item_id === pi.item_id)).map(pi => (
                  <button key={pi.item_id} type="button" className="btn btn-secondary" style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}
                    onClick={() => addCustomItem(pi)}>
                    <Plus size={12} /> {pi.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── grand total ── */}
          <div style={{ borderTop: '2px solid var(--primary-color)', paddingTop: '1rem', marginTop: '1rem', textAlign: 'right' }}>
            {Number(formData.buffalo_milk_price) > 0 && ((Number(formData.morning_buffalo_milk) || 0) + (Number(formData.evening_buffalo_milk) || 0)) > 0 && (
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                Buffalo milk: {((Number(formData.morning_buffalo_milk) || 0) + (Number(formData.evening_buffalo_milk) || 0)).toFixed(1)}L × ₹{Number(formData.buffalo_milk_price).toFixed(2)} = {formatCurrency(buffaloTotal())}
              </div>
            )}
            {Number(formData.cow_milk_price) > 0 && ((Number(formData.morning_cow_milk) || 0) + (Number(formData.evening_cow_milk) || 0)) > 0 && (
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                Cow milk: {((Number(formData.morning_cow_milk) || 0) + (Number(formData.evening_cow_milk) || 0)).toFixed(1)}L × ₹{Number(formData.cow_milk_price).toFixed(2)} = {formatCurrency(cowTotal())}
              </div>
            )}
            {chickTotal() > 0 && <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Chick: {formatCurrency(chickTotal())}</div>}
            {fatwaTotal() > 0 && <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Fatwa: {formatCurrency(fatwaTotal())}</div>}
            {customTotal() > 0 && <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Other items: {formatCurrency(customTotal())}</div>}
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--success-color)', marginTop: '0.5rem' }}>
              Grand Total: {formatCurrency(grandTotal())}
            </div>
          </div>

          <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
            <button type="button" className="btn btn-secondary" onClick={() => { setCurrentView('list'); setEditingItem(null); }} disabled={loading}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              <Check size={18} /> {loading ? 'Saving...' : (editingItem ? 'Update Sale' : 'Add Sale')}
            </button>
          </div>
        </div>
        )}

        {!editingItem && entryMode === 'vehicle' && (
          <div className="card" style={{ maxWidth: '1100px', margin: '0 auto' }}>

            {/* ── vehicle + date + slot ── */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Vehicle Number *</label>
                <select
                  className="form-select"
                  value={vehicleForm.vehicle_number}
                  onChange={e => setVehicleForm(p => ({ ...p, vehicle_number: e.target.value }))}
                  disabled={vehicleLoading || vehicleSubmitting}
                >
                  <option value="">Select Vehicle</option>
                  {vehicleNumbers.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input type="date" max={TODAY_DATE} className="form-input" value={vehicleForm.date}
                  onChange={e => setVehicleForm(p => ({ ...p, date: e.target.value }))} disabled={vehicleLoading || vehicleSubmitting} />
              </div>
              <div className="form-group">
                <label className="form-label">Slot *</label>
                <div style={{ display: 'flex', gap: '1rem', height: '42px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                    <input type="radio" name="vehicleSlot" value="morning" checked={vehicleForm.slot === 'morning'}
                      onChange={() => setVehicleForm(p => ({ ...p, slot: 'morning' }))} disabled={vehicleLoading || vehicleSubmitting} />
                    Morning
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                    <input type="radio" name="vehicleSlot" value="evening" checked={vehicleForm.slot === 'evening'}
                      onChange={() => setVehicleForm(p => ({ ...p, slot: 'evening' }))} disabled={vehicleLoading || vehicleSubmitting} />
                    Evening
                  </label>
                </div>
              </div>
            </div>

            {!vehicleForm.vehicle_number ? (
              <div className="no-data"><p>Select a vehicle number to see its customers.</p></div>
            ) : vehicleLoading ? (
              <div className="no-data"><p>Loading customers…</p></div>
            ) : vehicleRows.length === 0 ? (
              <div className="no-data"><p>No customers are assigned to this vehicle.</p></div>
            ) : (
              <>
                <div className="table-container" style={{ boxShadow: 'none', border: '1px solid var(--border-color)' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Buffalo (L)</th>
                        <th>Cow (L)</th>
                        <th>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehicleRows.map(row => (
                        <tr key={row.customer_id}>
                          <td style={{ fontWeight: 500 }}>
                            {row.customer_name}
                            {row.slot_filled && (
                              <span title={`Already has a ${vehicleForm.slot} entry on ${formatDate(vehicleForm.date)}`}
                                style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0.4rem', color: 'var(--warning-color, #b45309)' }}>
                                <AlertTriangle size={14} />
                              </span>
                            )}
                          </td>
                          <td>
                            <input type="number" className="form-input" style={{ width: '90px' }} min="0" step="0.1" placeholder="0"
                              value={row.buffalo_quantity} onChange={e => updateVehicleRow(row.customer_id, 'buffalo_quantity', e.target.value)} disabled={vehicleSubmitting} />
                          </td>
                          <td>
                            <input type="number" className="form-input" style={{ width: '90px' }} min="0" step="0.1" placeholder="0"
                              value={row.cow_quantity} onChange={e => updateVehicleRow(row.customer_id, 'cow_quantity', e.target.value)} disabled={vehicleSubmitting} />
                          </td>
                          <td>
                            <button type="button" className="btn-icon" title="Edit prices / chick / fatwa / other items"
                              onClick={() => setVehicleEditRow(row.customer_id)} disabled={vehicleSubmitting}>
                              <Edit size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {(() => {
                  const totalBuffalo = vehicleRows.reduce((s, r) => s + (Number(r.buffalo_quantity) || 0), 0);
                  const totalCow = vehicleRows.reduce((s, r) => s + (Number(r.cow_quantity) || 0), 0);
                  return (
                    <div style={{ marginTop: '1rem', padding: '0.75rem 1.2rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)', display: 'flex', gap: '2rem' }}>
                      <div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Total Buffalo (L)</div>
                        <div style={{ fontWeight: 700 }}>{totalBuffalo.toFixed(1)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Total Cow (L)</div>
                        <div style={{ fontWeight: 700 }}>{totalCow.toFixed(1)}</div>
                      </div>
                    </div>
                  );
                })()}

                <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => { setCurrentView('list'); setEditingItem(null); }} disabled={vehicleSubmitting}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={handleVehicleSubmit} disabled={vehicleSubmitting}>
                    <Check size={18} /> {vehicleSubmitting ? 'Saving...' : 'Add Sale'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── per-row edit overlay (vehicle mode) ── */}
        {vehicleEditRow !== null && (() => {
          const row = vehicleRows.find(r => r.customer_id === vehicleEditRow);
          if (!row) return null;
          return (
            <div style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <div className="card" style={{ width: '480px', maxHeight: '85vh', overflowY: 'auto', padding: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>{row.customer_name}</h3>
                  <button className="btn-icon" onClick={() => setVehicleEditRow(null)} aria-label="Close"><X size={18} /></button>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Buffalo Milk Price (₹/L)</label>
                    <input type="number" className="form-input" min="0" step="0.01"
                      placeholder={row.default_buffalo_price ?? '0.00'}
                      value={row.buffalo_price_override}
                      onChange={e => updateVehicleRow(row.customer_id, 'buffalo_price_override', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cow Milk Price (₹/L)</label>
                    <input type="number" className="form-input" min="0" step="0.01"
                      placeholder={row.default_cow_price ?? '0.00'}
                      value={row.cow_price_override}
                      onChange={e => updateVehicleRow(row.customer_id, 'cow_price_override', e.target.value)} />
                  </div>
                </div>

                <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
                  <h4 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>Chick</h4>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Quantity</label>
                    <input type="number" className="form-input" min="0" step="0.01" placeholder="0"
                      value={row.chick_quantity} onChange={e => updateVehicleRow(row.customer_id, 'chick_quantity', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price per unit (₹)</label>
                    <input type="number" className="form-input" min="0" step="0.01" placeholder={row.default_chick_price ?? '0.00'}
                      value={row.chick_price_override} onChange={e => updateVehicleRow(row.customer_id, 'chick_price_override', e.target.value)} />
                  </div>
                </div>

                <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
                  <h4 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>Fatwa</h4>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Quantity</label>
                    <input type="number" className="form-input" min="0" step="0.01" placeholder="0"
                      value={row.fatwa_quantity} onChange={e => updateVehicleRow(row.customer_id, 'fatwa_quantity', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price per unit (₹)</label>
                    <input type="number" className="form-input" min="0" step="0.01" placeholder={row.default_fatwa_price ?? '0.00'}
                      value={row.fatwa_price_override} onChange={e => updateVehicleRow(row.customer_id, 'fatwa_price_override', e.target.value)} />
                  </div>
                </div>

                {productItems.length > 0 && (
                  <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
                    <h4 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>Other Items</h4>
                    {row.custom_items.map((ci, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: '2', minWidth: '100px' }}>
                          <label className="form-label" style={{ fontSize: '0.8rem' }}>Item</label>
                          <input className="form-input" value={ci.item_name} readOnly style={{ background: 'var(--bg-secondary)' }} />
                        </div>
                        <div style={{ flex: '1', minWidth: '70px' }}>
                          <label className="form-label" style={{ fontSize: '0.8rem' }}>Qty ({ci.unit})</label>
                          <input type="number" className="form-input" value={ci.quantity} min="0" step="0.01"
                            onChange={e => updateVehicleRowCustomItem(row.customer_id, idx, 'quantity', e.target.value)} />
                        </div>
                        <div style={{ flex: '1', minWidth: '80px' }}>
                          <label className="form-label" style={{ fontSize: '0.8rem' }}>Price</label>
                          <input type="number" className="form-input" value={ci.price_per_unit} min="0" step="0.01"
                            onChange={e => updateVehicleRowCustomItem(row.customer_id, idx, 'price_per_unit', e.target.value)} />
                        </div>
                        <button type="button" className="btn-icon" style={{ color: 'var(--error-color)', marginBottom: '2px' }}
                          onClick={() => removeVehicleRowCustomItem(row.customer_id, idx)}>
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                      {productItems.filter(pi => !row.custom_items.find(ci => ci.product_item_id === pi.item_id)).map(pi => (
                        <button key={pi.item_id} type="button" className="btn btn-secondary" style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}
                          onClick={() => addVehicleRowCustomItem(row.customer_id, pi)}>
                          <Plus size={12} /> {pi.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button className="btn btn-primary" onClick={() => setVehicleEditRow(null)}>Done</button>
                </div>
              </div>
            </div>
          );
        })()}
      </>
    );
  }

  /* ──────────────────────────────────────────────────────────────────────
   *  LIST VIEW
   * ────────────────────────────────────────────────────────────────────── */
  return renderLayout(
    <>
      {/* replicate modal */}
      {replicateItem && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className="card" style={{ width: '380px', padding: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Replicate Sale</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Copying sale #{replicateItem.sr_no || replicateItem.bill_no} for {getCustomerName(replicateItem.customer_id)}.
              All quantities and prices will be duplicated.
            </p>

            <div className="form-group" style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 500 }}>
                <input type="radio" name="replicateMode" value="single" checked={replicateMode === 'single'} onChange={() => setReplicateMode('single')} />
                Single Entry
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 500 }}>
                <input type="radio" name="replicateMode" value="range" checked={replicateMode === 'range'} onChange={() => setReplicateMode('range')} />
                Multiple Entries (Range)
              </label>
            </div>

            {replicateMode === 'single' ? (
              <div className="form-group">
                <label className="form-label">New Sale Date *</label>
                <input type="date" max={TODAY_DATE} className="form-input" value={replicateDate} onChange={e => setReplicateDate(e.target.value)} />
              </div>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Start Date *</label>
                    <input type="date" max={TODAY_DATE} className="form-input" value={replicateStartDate} onChange={e => setReplicateStartDate(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Date *</label>
                    <input type="date" max={TODAY_DATE} className="form-input" value={replicateEndDate} onChange={e => setReplicateEndDate(e.target.value)} />
                  </div>
                </div>
                <div style={{ fontSize: '0.85rem', color: replicateRangeCount() > 0 ? 'var(--text-secondary)' : 'var(--error)', marginBottom: '0.5rem' }}>
                  {replicateRangeCount() > 0
                    ? `${replicateRangeCount()} ${replicateRangeCount() === 1 ? 'entry' : 'entries'} will be created (one per day, inclusive).`
                    : 'End date must be on or after start date.'}
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-secondary" onClick={() => setReplicateItem(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleReplicate}
                disabled={loading || (replicateMode === 'range' && replicateRangeCount() <= 0)}
              >
                {loading ? 'Saving...' : replicateMode === 'range' ? `Create ${replicateRangeCount()} Entries` : 'Replicate'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1 className="page-title">Sales History</h1>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-secondary" onClick={openExport} disabled={loading}>
            <Download size={18} /> Export
          </button>
          <button className="btn btn-primary" onClick={handleAdd} disabled={loading}>
            <Plus size={18} /> Add Sale
          </button>
        </div>
      </div>

      {error && (
        <div className="error-container">
          <p>{error}</p>
          <button className="btn btn-primary btn-sm" onClick={() => loadData(currentPage)}>Retry</button>
        </div>
      )}

      {/* filters */}
      <div className="filter-bar">
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Customer</label>
          <select name="customer_id" value={filters.customer_id} onChange={e => setFilters(p => ({ ...p, customer_id: e.target.value }))} className="form-select" style={{ minWidth: '140px' }}>
            <option value="">All Customers</option>
            {sortedByName(customers).map(c => <option key={c.customer_id} value={c.customer_id}>{customerOptionLabel(c)}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Start Date</label>
          <input type="date" max={TODAY_DATE} name="start_date" value={filters.start_date} onChange={e => setFilters(p => ({ ...p, start_date: e.target.value }))} className="form-input" />
        </div>
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>End Date</label>
          <input type="date" max={TODAY_DATE} name="end_date" value={filters.end_date} onChange={e => setFilters(p => ({ ...p, end_date: e.target.value }))} className="form-input" />
        </div>
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Min Amount</label>
          <input type="number" name="min_amount" value={filters.min_amount} onChange={e => setFilters(p => ({ ...p, min_amount: e.target.value }))} placeholder="Min" className="form-input" min="0" style={{ width: '90px' }} />
        </div>
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Max Amount</label>
          <input type="number" name="max_amount" value={filters.max_amount} onChange={e => setFilters(p => ({ ...p, max_amount: e.target.value }))} placeholder="Max" className="form-input" min="0" style={{ width: '90px' }} />
        </div>
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Status</label>
          <select name="status" value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))} className="form-select">
            <option value="">All</option>
            <option value="billed">Billed</option>
            <option value="unbilled">Unbilled</option>
          </select>
        </div>
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Vehicle</label>
          <select name="vehicle_number" value={filters.vehicle_number} onChange={e => setFilters(p => ({ ...p, vehicle_number: e.target.value }))} className="form-select">
            <option value="">All Vehicles</option>
            {vehicleNumbers.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <button className="btn btn-secondary" onClick={() => setFilters({ customer_id: '', start_date: '', end_date: '', min_amount: '', max_amount: '', status: '', vehicle_number: '' })} style={{ height: '42px', alignSelf: 'flex-end' }}>Clear</button>
      </div>

      {!selectMode ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
          <BulkSelectToggle onClick={enterSelectMode} disabled={loading || data.length === 0} />
        </div>
      ) : (
        <BulkSelectBar
          selectedCount={selectedIds.size}
          onDelete={requestBulkDelete}
          onCancel={exitSelectMode}
          disabled={loading || bulkDeleting}
        />
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {selectMode && (
                <th className="select-cell">
                  <input type="checkbox" className="row-checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
              )}
              <th>
                <button
                  type="button"
                  onClick={() => setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit', padding: 0 }}
                  title={sortOrder === 'asc' ? 'Sorted oldest first — click for newest first' : 'Sorted newest first — click for oldest first'}
                >
                  Date {sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              </th>
              <th>Customer</th>
              <th>Morning Buffalo</th>
              <th>Morning Cow</th>
              <th>Evening Buffalo</th>
              <th>Evening Cow</th>
              <th>Chick</th>
              <th>Fatwa</th>
              <th>Other</th>
              <th>Total</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map(item => (
              <tr key={item.bill_no}>
                {selectMode && (
                  <td className="select-cell">
                    <input type="checkbox" className="row-checkbox" checked={selectedIds.has(item.bill_no)} onChange={() => toggleRow(item.bill_no)} />
                  </td>
                )}
                <td>{formatDate(item.date)}</td>
                <td style={{ fontWeight: 500 }}>{getCustomerName(item.customer_id)}</td>
                <td>{item.morning_buffalo_milk ? `${item.morning_buffalo_milk}L` : '-'}</td>
                <td>{item.morning_cow_milk ? `${item.morning_cow_milk}L` : '-'}</td>
                <td>{item.evening_buffalo_milk ? `${item.evening_buffalo_milk}L` : '-'}</td>
                <td>{item.evening_cow_milk ? `${item.evening_cow_milk}L` : '-'}</td>
                <td>{item.chick_quantity > 0 ? item.chick_quantity : '-'}</td>
                <td>{item.fatwa_quantity > 0 ? item.fatwa_quantity : '-'}</td>
                <td>{(item.custom_items || []).length > 0 ? `${item.custom_items.length} item(s)` : '-'}</td>
                <td style={{ fontWeight: 600, color: 'var(--success-color)' }}>{formatCurrency(item.total_amount)}</td>
                <td>
                  <span className={`status-badge ${item.invoice_id ? 'paid' : 'due'}`} title={item.invoice_id ? `Invoiced (Invoice #${item.invoice_id})` : 'Not yet billed'}>
                    {item.invoice_id ? 'Billed' : 'Unbilled'}
                  </span>
                </td>
                <td>
                  <div className="actions-cell">
                    <button className="btn-icon" onClick={() => handleEdit(item)} disabled={loading} title="Edit"><Edit size={16} /></button>
                    <button className="btn-icon" onClick={() => {
                      setReplicateItem(item);
                      setReplicateMode('single');
                      const today = new Date().toISOString().split('T')[0];
                      setReplicateDate(today);
                      setReplicateStartDate(today);
                      setReplicateEndDate(today);
                    }} disabled={loading} title="Replicate"><Copy size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && !loading && <div className="no-data"><p>No sale records found</p></div>}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button className="pagination-btn" onClick={() => loadData(currentPage - 1)} disabled={currentPage === 1 || loading}><ChevronLeft size={16} /></button>
          {[...Array(Math.min(totalPages, 5))].map((_, i) => {
            let p = totalPages <= 5 ? i + 1 : currentPage <= 3 ? i + 1 : currentPage >= totalPages - 2 ? totalPages - 4 + i : currentPage - 2 + i;
            return (
              <button key={p} className={`pagination-btn ${currentPage === p ? 'active' : ''}`} onClick={() => loadData(p)} disabled={loading}>{p}</button>
            );
          })}
          <button className="pagination-btn" onClick={() => loadData(currentPage + 1)} disabled={currentPage === totalPages || loading}><ChevronRight size={16} /></button>
        </div>
      )}
      {(() => {
        const stat = (label, value) => (
          <div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{label}</div>
            <div style={{ fontWeight: 700 }}>{value > 0 ? value.toFixed(2) : '-'}</div>
          </div>
        );

        if (!overallStats) {
          return (
            <div style={{ marginTop: '1rem', padding: '1rem 1.2rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
              {loadingStats ? 'Calculating totals…' : 'Totals unavailable.'}
            </div>
          );
        }

        return (
          <div style={{ marginTop: '1rem', padding: '1rem 1.2rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', opacity: loadingStats ? 0.6 : 1 }}>
            {stat('Morning Buffalo (L)', overallStats.morningBuffalo)}
            {stat('Morning Cow (L)', overallStats.morningCow)}
            {stat('Evening Buffalo (L)', overallStats.eveningBuffalo)}
            {stat('Evening Cow (L)', overallStats.eveningCow)}
            {stat('Buffalo Milk Total (L)', overallStats.buffaloTotalL)}
            {stat('Cow Milk Total (L)', overallStats.cowTotalL)}
            {stat('Chick Total (qty)', overallStats.chickQtyTotal)}
            {stat('Fatwa Total (qty)', overallStats.fatwaQtyTotal)}
            {stat('Other Items Total (qty)', overallStats.otherQtyTotal)}
            <div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Sum Amount</div>
              <div style={{ fontWeight: 700, color: 'var(--success-color)' }}>{formatCurrency(overallStats.sumAmount)}</div>
            </div>
          </div>
        );
      })()}

      <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
        Showing {data.length} of {totalItems} records (Page {currentPage} of {totalPages})
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        title={confirmDelete?.invoicedCount > 0 ? 'Invoiced records included' : 'Delete selected records?'}
        message={
          confirmDelete?.invoicedCount > 0
            ? `${confirmDelete.invoicedCount} of the ${confirmDelete.ids.length} selected record(s) are already part of a generated invoice. Deleting them will remove them from that invoice and reduce its total. This cannot be undone. Continue?`
            : `Delete ${confirmDelete?.ids.length || 0} selected record(s)? This cannot be undone.`
        }
        confirmLabel="Delete"
        danger
        loading={bulkDeleting}
        onConfirm={confirmBulkDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      <ExportModal open={showExport} onClose={() => setShowExport(false)} onExport={handleExport} loading={exporting} title="Export Sales to CSV">
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Customer</label>
          <select className="form-select" value={exportFilters.customer_id} onChange={e => setExportFilters(p => ({ ...p, customer_id: e.target.value }))}>
            <option value="">All Customers</option>
            {sortedByName(customers).map(c => <option key={c.customer_id} value={c.customer_id}>{customerOptionLabel(c)}</option>)}
          </select>
        </div>
        <div className="form-row" style={{ marginBottom: 0 }}>
          <div className="form-group">
            <label className="form-label">Start Date</label>
            <input type="date" max={TODAY_DATE} className="form-input" value={exportFilters.start_date} onChange={e => setExportFilters(p => ({ ...p, start_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">End Date</label>
            <input type="date" max={TODAY_DATE} className="form-input" value={exportFilters.end_date} onChange={e => setExportFilters(p => ({ ...p, end_date: e.target.value }))} />
          </div>
        </div>
        <div className="form-row" style={{ marginBottom: 0 }}>
          <div className="form-group">
            <label className="form-label">Min Amount</label>
            <input type="number" min="0" className="form-input" value={exportFilters.min_amount} onChange={e => setExportFilters(p => ({ ...p, min_amount: e.target.value }))} placeholder="Min" />
          </div>
          <div className="form-group">
            <label className="form-label">Max Amount</label>
            <input type="number" min="0" className="form-input" value={exportFilters.max_amount} onChange={e => setExportFilters(p => ({ ...p, max_amount: e.target.value }))} placeholder="Max" />
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Status</label>
          <select className="form-select" value={exportFilters.status} onChange={e => setExportFilters(p => ({ ...p, status: e.target.value }))}>
            <option value="">All</option>
            <option value="billed">Billed</option>
            <option value="unbilled">Unbilled</option>
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Vehicle</label>
          <select className="form-select" value={exportFilters.vehicle_number} onChange={e => setExportFilters(p => ({ ...p, vehicle_number: e.target.value }))}>
            <option value="">All Vehicles</option>
            {vehicleNumbers.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </ExportModal>
    </>
  );
}

export default SalesPage;
